import { format, parseISO } from 'date-fns';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { Platform, SectionList, SectionListData, View } from 'react-native';
import { StyleSheet } from 'react-native';

import { RewardsPointsProgress } from '@fhs/loyalty/src/components';
import { ErrorCard, IconReceiptLongOutline, Skeleton, Text, createMqStyles, tokens } from '@fhs/ui';
import { ITransactionFragment } from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useLoyaltyUserTransactions } from '../../queries/loyalty.queries';

import { TransactionItem } from './transaction-item';
import { TransactionItemSkeleton } from './transaction-item-skeleton';

type TransactionSection =
  | {
      isLoading: false;
      title: string;
      data: ITransactionFragment[];
    }
  | {
      isLoading: true;
      title: string;
      data: any[];
    };
const createSections = (transactions: ITransactionFragment[] | undefined): TransactionSection[] => {
  if (!transactions) {
    return [];
  }

  // Group transactions by month
  const groupedTransactions =
    transactions?.reduce(
      (groups: { [key: string]: TransactionSection }, transaction: ITransactionFragment) => {
        const date = parseISO(transaction.createdAt);
        const monthKey = format(date, 'yyyy-MM');
        const monthTitle = format(date, 'MMMM');

        if (!groups[monthKey]) {
          groups[monthKey] = {
            isLoading: false,
            title: monthTitle,
            data: [],
          };
        }

        groups[monthKey].data.push(transaction);
        return groups;
      },
      {}
    ) ?? {};

  const sections = Object.values(groupedTransactions) as TransactionSection[];
  sections.sort((a, b) => {
    const dateA = parseISO(a.data[0].createdAt);
    const dateB = parseISO(b.data[0].createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  return sections;
};

const renderItem = ({
  item,
  section,
}: {
  item: ITransactionFragment;
  section: SectionListData<any, TransactionSection>;
}) => {
  if (section.isLoading) {
    return <TransactionItemSkeleton />;
  }
  return <TransactionItem transaction={item} />;
};

const renderSeparator = () => {
  return <View style={styles.separator} />;
};

const renderSectionFooter = () => {
  return <View style={styles.sectionFooter} />;
};

const renderFooter = () => {
  return <View style={styles.footer} />;
};

const loadingSections: TransactionSection[] = [
  {
    isLoading: true,
    title: '',
    data: Array.from({ length: 6 }, () => ({})),
  },
  {
    isLoading: true,
    title: '',
    data: Array.from({ length: 3 }, () => ({})),
  },
  {
    isLoading: true,
    title: '',
    data: Array.from({ length: 3 }, () => ({})),
  },
];

export function RewardsHistoryScreen() {
  const mqStyles = useMqStyles();
  const { loyaltyUserReady } = useLoyaltyLegacyStates();
  const {
    data: loyaltyUserTransactions,
    isLoading: loyaltyUserTransactionsLoading,
    refetch,
    isError,
  } = useLoyaltyUserTransactions();
  const isLoading = loyaltyUserTransactionsLoading || !loyaltyUserReady;

  const pointsProgressRef = useRef<View>(null);

  // Web: move focus to the points summary after real content replaces skeletons. useLayoutEffect
  // runs after the SectionList commits the list header; rAF retries cover RN Web batching where the
  // ref may not be focusable on the first synchronous pass.
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || isLoading) {
      return;
    }
    let cancelled = false;
    let raf2 = 0;
    const focusPointsProgress = () => {
      if (cancelled) {
        return;
      }
      (pointsProgressRef.current as unknown as { focus?: () => void } | null)?.focus?.();
    };
    focusPointsProgress();
    const raf1 = requestAnimationFrame(() => {
      if (cancelled) {
        return;
      }
      focusPointsProgress();
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) {
          focusPointsProgress();
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isLoading]);

  const renderHeader = useCallback(
    () => <RewardsPointsProgress ref={pointsProgressRef} style={mqStyles.pointsProgress} />,
    [mqStyles.pointsProgress]
  );

  const renderSectionHeader = useCallback(
    ({
      section: { title, isLoading: sectionLoading },
    }: {
      section: SectionListData<ITransactionFragment, TransactionSection>;
    }) => {
      if (sectionLoading) {
        return (
          <View
            accessible={false}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            style={mqStyles.sectionHeader}
          >
            <Skeleton style={mqStyles.sectionHeaderSkeleton} />
          </View>
        );
      }
      return (
        <View
          accessibilityLabel={title}
          accessibilityRole="header"
          accessible
          focusable
          style={mqStyles.sectionHeader}
          {...(Platform.OS === 'web'
            ? ({
                tabIndex: 0,
              } as Record<string, unknown>)
            : {})}
        >
          <Text.Heading accessible={false} type="three">
            {title}
          </Text.Heading>
        </View>
      );
    },
    [mqStyles.sectionHeader, mqStyles.sectionHeaderSkeleton]
  );

  const sections = useMemo(
    () => createSections(loyaltyUserTransactions),
    [loyaltyUserTransactions]
  );

  const renderEmpty = useCallback(() => {
    return (
      <View
        accessibilityLabel="No rewards transactions. Redeem a reward online or in-restaurant to see it in your transaction history."
        accessibilityRole="text"
        accessible
        style={mqStyles.emptyContainer}
      >
        <IconReceiptLongOutline
          accessibilityElementsHidden
          aria-hidden
          importantForAccessibility="no-hide-descendants"
          size={80}
        />
        <Text.Heading accessible={false} type="two" style={styles.emptyTitle}>
          No Rewards Transactions
        </Text.Heading>
        <Text.Ui accessible={false} size="md" style={styles.emptyDescription}>
          Redeem a reward online or in-restaurant to see it in your transaction history.
        </Text.Ui>
      </View>
    );
  }, [mqStyles.emptyContainer]);

  if (isError) {
    return <ErrorCard buttonHandle={refetch} />;
  }

  return (
    <SectionList
      style={styles.page}
      contentContainerStyle={mqStyles.contentContainer}
      sections={isLoading ? loadingSections : sections}
      stickySectionHeadersEnabled={false}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      renderSectionFooter={renderSectionFooter}
      ListHeaderComponent={renderHeader}
      ItemSeparatorComponent={renderSeparator}
      ListFooterComponent={renderFooter}
      ListEmptyComponent={renderEmpty}
    />
  );
}

const useMqStyles = createMqStyles({
  contentContainer: {
    $base: {
      flexGrow: 1,
    },
    $gteDesktop: {
      width: 908,
      alignSelf: 'center',
    },
  },
  sectionHeader: {
    $base: {
      paddingHorizontal: 16,
    },
    $gteDesktop: {
      paddingHorizontal: 0,
    },
  },
  sectionHeaderSkeleton: {
    $base: {
      marginHorizontal: 16,
      height: 20,
      width: 80,
    },
    $gteDesktop: {
      marginHorizontal: 0,
    },
  },
  pointsProgress: {
    $base: {
      paddingHorizontal: 16,
      marginBottom: 30,
    },
    $gteDesktop: {
      paddingHorizontal: 0,
    },
  },
  emptyContainer: {
    $base: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 100,
    },
    $gteDesktop: {
      paddingHorizontal: 0,
    },
  },
});

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: tokens.colors.$white,
    paddingTop: 20,
  },
  separator: {
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  sectionFooter: {
    width: '100%',
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity04,
    marginBottom: 20,
  },
  footer: {
    marginBottom: 40,
  },
  emptyTitle: {
    marginTop: 16,
    textAlign: 'center',
  },
  emptyDescription: {
    marginTop: 8,
    textAlign: 'center',
  },
});
